import { useCallback, useSyncExternalStore } from 'react';

const MAX_RETAINED_CANCELLED_CONVERSATIONS = 100;

const cancelledConversationIds = new Set<string>();
const subscribers = new Set<() => void>();

const notifySubscribers = (): void => {
	for (const subscriber of subscribers) {
		try {
			subscriber();
		} catch {
			// A subscriber observes cancellation; it must not affect stopping a stream.
		}
	}
};

const subscribe = (subscriber: () => void): (() => void) => {
	subscribers.add(subscriber);
	return () => subscribers.delete(subscriber);
};

/**
 * Marks a conversation as stopped until its next real send starts.
 *
 * This state intentionally stays tab-local. It protects inline consumers from late stream or query
 * updates without retaining an unbounded history of conversations.
 */
export const setConversationCancelled = (conversationId: string, cancelled: boolean): void => {
	if (!conversationId) {
		return;
	}

	if (!cancelled) {
		if (cancelledConversationIds.delete(conversationId)) {
			notifySubscribers();
		}
		return;
	}

	if (cancelledConversationIds.has(conversationId)) {
		return;
	}

	cancelledConversationIds.add(conversationId);
	if (cancelledConversationIds.size > MAX_RETAINED_CANCELLED_CONVERSATIONS) {
		const oldestConversationId = cancelledConversationIds.values().next().value;
		if (oldestConversationId) {
			cancelledConversationIds.delete(oldestConversationId);
		}
	}
	notifySubscribers();
};

/** Reads a pending stop before a background send has created its stream controller. */
export const isConversationCancelled = (conversationId: string): boolean =>
	Boolean(conversationId && cancelledConversationIds.has(conversationId));

/** Reactively reads whether a conversation has been stopped in this tab. */
export const useIsConversationCancelled = (conversationId: string): boolean => {
	const getSnapshot = useCallback(() => isConversationCancelled(conversationId), [conversationId]);
	const subscribeToConversation = useCallback(
		(subscriber: () => void) => (conversationId ? subscribe(subscriber) : () => {}),
		[conversationId],
	);

	return useSyncExternalStore(subscribeToConversation, getSnapshot, getSnapshot);
};
